import { useEffect, useState, useCallback } from "react";
import { Mail, Phone, Calendar, Car, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
}

export default function MessagesViewer() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/contact/messages");
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Messages</h2>
          <div className="tech-label">Contact form submissions from your website</div>
        </div>
        <Button onClick={load} variant="outline" className="rounded-none border-zinc-300" data-testid="messages-refresh">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading && items.length === 0 ? (
        <div className="bg-white border border-zinc-300 p-12 text-center tech-label">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-300 p-12 text-center">
          <Mail size={32} className="mx-auto text-zinc-400 mb-3" />
          <div className="font-display font-bold text-lg text-zinc-900">No messages yet</div>
          <div className="tech-label mt-1">When customers submit the contact form, they'll appear here.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 bg-white border border-zinc-300 max-h-[70vh] overflow-y-auto">
            {items.map((m, idx) => (
              <button
                key={m.id}
                onClick={() => setSelected(m)}
                data-testid={`message-item-${idx}`}
                className={`w-full text-left p-4 border-b border-zinc-200 hover:bg-zinc-50 transition-colors flex flex-col gap-1 ${selected?.id === m.id ? "bg-zinc-50" : ""}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display font-bold text-base text-zinc-900 truncate">{m.name}</div>
                  {m.delivered ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                      <CheckCircle2 size={10} /> Sent
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                      <AlertCircle size={10} /> Stored
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-600 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Phone size={11} /> {m.phone}</span>
                  {m.vehicle && <span className="inline-flex items-center gap-1 truncate"><Car size={11} /> {m.vehicle}</span>}
                </div>
                <div className="text-xs text-zinc-500 line-clamp-2">{m.message}</div>
                <div className="text-[10px] tracking-wider uppercase text-zinc-400 mt-1">
                  {formatDate(m.created_at)}
                </div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {selected ? (
              <div className="bg-white border border-zinc-300 p-6 sticky top-24" data-testid="message-detail">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="tech-label">From</div>
                    <div className="font-display font-extrabold text-2xl text-zinc-900 tracking-tight">{selected.name}</div>
                  </div>
                  {selected.delivered ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
                      <CheckCircle2 size={12} /> Emailed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
                      <AlertCircle size={12} /> Stored only
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
                  <div>
                    <div className="tech-label !text-[10px]">Phone</div>
                    <a href={`tel:${selected.phone}`} className="font-display font-bold text-lg text-zinc-900 hover:text-brand-red">{selected.phone}</a>
                  </div>
                  <div>
                    <div className="tech-label !text-[10px]">Email</div>
                    {selected.email ? (
                      <a href={`mailto:${selected.email}`} className="text-zinc-900 hover:text-brand-red break-all">{selected.email}</a>
                    ) : (
                      <div className="text-zinc-400">—</div>
                    )}
                  </div>
                  <div>
                    <div className="tech-label !text-[10px]">Vehicle</div>
                    <div className="text-zinc-900">{selected.vehicle || "—"}</div>
                  </div>
                  <div>
                    <div className="tech-label !text-[10px] flex items-center gap-1"><Calendar size={10} /> Received</div>
                    <div className="text-zinc-900">{formatDate(selected.created_at)}</div>
                  </div>
                </div>

                <div className="border-t border-zinc-200 pt-4">
                  <div className="tech-label !text-[10px] mb-2">Message</div>
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{selected.message}</div>
                </div>

                <div className="mt-6 flex gap-2 flex-wrap">
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redHover text-white px-4 py-2 font-bold text-sm">
                    <Phone size={14} /> Call back
                  </a>
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 font-bold text-sm">
                      <Mail size={14} /> Reply by email
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-zinc-300 p-12 text-center text-zinc-400 text-sm">
                Select a message on the left to view full details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
